/**
 * UIManager.js - Unified UI Management
 *
 * Coordinates all UI subsystems:
 * - NotificationSystem - In-game notifications
 * - SplashScreen - Lobby and ready screen
 * - GameEndScreen - Victory/defeat screen
 *
 * Provides a unified interface for all UI operations.
 */

import { AccountScreen } from './AccountScreen.js';
import { GameEndScreen } from './GameEndScreen.js';
import { HeaderNav } from './HeaderNav.js';
import { LeaderboardScreen } from './LeaderboardScreen.js';
import { LockerScreen } from './LockerScreen.js';
import { PixiRenderer } from '../rendering/pixi/PixiRenderer.js';
import { NotificationSystem } from './NotificationSystem.js';
import { SplashScreen } from './SplashScreen.js';

const UIManager = {
  // Sub-system references
  notifications: null,
  splashScreen: null,
  gameEndScreen: null,
  lockerScreen: null,
  accountScreen: null,
  leaderboardScreen: null,
  headerNav: null,

  // Which lobby screen is showing ('play' means the splash with nothing over it)
  activeScreen: 'play',

  // The signed-in user, or null for a guest — decides whether Account exists
  authUser: null,

  // Game state reference
  gameState: null,

  // Cursor visibility state
  cursorHidden: false,

  // Callbacks for game actions
  callbacks: {
    onReady: null,
    onUnready: null,
    onReturnToLobby: null,
    onLocker: null,
    onSpectate: null,
    onSubmitFlagNote: null
  },

  /**
   * Initialize all UI systems
   * @param {Object} options - Configuration options
   * @param {Object} options.gameState - Game state reference
   * @param {Object} options.callbacks - Callback functions
   */
  init(options = {}) {
    this.gameState = options.gameState;
    Object.assign(this.callbacks, options.callbacks || {});

    // Initialize NotificationSystem
    NotificationSystem.init({
      debounceMap: this.gameState?.notificationDebounce || new Map(),
      debounceMs: this.gameState?.constructor?.NOTIFICATION_DEBOUNCE_MS || 2000
    });
    this.notifications = NotificationSystem;

    // Initialize SplashScreen
    SplashScreen.init({
      gameState: this.gameState,
      callbacks: {
        onReady: this.callbacks.onReady,
        onUnready: this.callbacks.onUnready,
        onLocker: this.callbacks.onLocker,
        showNotification: (msg, type) => this.showNotification(msg, type),
        isLoggedIn: () => Boolean(this.authUser)
      }
    });
    this.splashScreen = SplashScreen;

    // Initialize GameEndScreen
    GameEndScreen.init({
      callbacks: {
        onReturnToLobby: this.callbacks.onReturnToLobby,
        onSpectate: this.callbacks.onSpectate,
        onSubmitFlagNote: this.callbacks.onSubmitFlagNote
      }
    });
    this.gameEndScreen = GameEndScreen;

    // Locker (6A): collaborators are injected by client.js through options.locker
    if (options.locker) {
      LockerScreen.init({
        ...options.locker,
        showNotification: (msg, type) => this.showNotification(msg, type),
        onClose: () => { /* the splash was never hidden; nothing to restore */ }
      });
      this.lockerScreen = LockerScreen;
    }

    if (options.lobbyModes || options.defaultModeId) {
      this.setGameModes(options.lobbyModes, options.defaultModeId);
    }

    // Stats phase: the account and leaderboard screens are siblings behind the
    // header nav. fetchFn is injected so both run under jsdom with no network.
    const fetchFn = options.fetchFn || ((...args) => fetch(...args));

    AccountScreen.init({
      fetchFn,
      showNotification: (msg, type) => this.showNotification(msg, type),
      onClose: () => this.showScreen('play'),
      onDisplayNameChange: (displayName) => this.setAuthUser({ ...(this.authUser || {}), displayName })
    });
    this.accountScreen = AccountScreen;

    LeaderboardScreen.init({ fetchFn, onClose: () => this.showScreen('play') });
    this.leaderboardScreen = LeaderboardScreen;

    // One header nav in front of those siblings. HeaderNav paints #lockerButton,
    // which SplashScreen resolves lazily, so init order between them is free.
    this.authUser = options.user ?? null;
    HeaderNav.init({ user: this.authUser, onSelect: (screenId) => this.showScreen(screenId) });
    this.headerNav = HeaderNav;
    HeaderNav.show();
    this.showScreen('play');
  },

  /**
   * Show exactly one lobby screen; 'play' means the splash with nothing over it.
   * Screens are siblings, so opening one closes the others.
   * @param {'play'|'locker'|'account'|'leaderboard'} screenId
   */
  showScreen(screenId) {
    this.activeScreen = screenId;
    if (screenId !== 'locker') this.lockerScreen?.hide();
    if (screenId !== 'account') this.accountScreen?.hide();
    if (screenId !== 'leaderboard') this.leaderboardScreen?.hide();
    this.headerNav?.setActive(screenId);

    if (screenId === 'account') return this.accountScreen?.show();
    if (screenId === 'leaderboard') return this.leaderboardScreen?.show();
    if (screenId === 'locker') return this.lockerScreen?.show();
    return undefined;
  },

  /** Account is unreachable signed out, so this is a no-op for a guest. */
  openAccount() {
    if (!this.authUser) return undefined;
    return this.showScreen('account');
  },

  openLeaderboard() {
    return this.showScreen('leaderboard');
  },

  /**
   * Sign-in state changed, or the account's display name changed: the
   * Account nav item appears or disappears, and the lobby name field (what
   * Ready actually sends) is re-synced so a rename takes effect immediately.
   */
  setAuthUser(user) {
    this.authUser = user || null;
    this.headerNav?.setAuth(this.authUser);
    this.headerNav?.setActive(this.activeScreen);
    if (this.authUser?.displayName) this.splashScreen?.setPlayerName(this.authUser.displayName);
    this.splashScreen?.onAuthChanged?.();
  },

  /**
   * Toggle global cursor visibility for gameplay vs UI overlays
   * @param {boolean} hidden
   */
  setCursorHidden(hidden) {
    if (this.cursorHidden === hidden) return;
    this.cursorHidden = hidden;

    const body = document.body;
    if (body) {
      body.classList.toggle('game-cursor-hidden', hidden);
    }

    const baseCanvas = document.getElementById('gameCanvas');
    if (baseCanvas) {
      baseCanvas.style.cursor = hidden ? 'none' : 'auto';
    }

    if (PixiRenderer.app?.canvas) {
      PixiRenderer.app.canvas.style.cursor = hidden ? 'none' : 'auto';
    }
  },

  // ─────────────────────────────────────────────────────────────
  // NOTIFICATION SHORTCUTS
  // ─────────────────────────────────────────────────────────────

  /**
   * Show a notification
   * @param {string} message - Message to display
   * @param {string} type - Type: 'info', 'warning', 'error', 'success'
   * @param {number} duration - Duration in ms
   */
  showNotification(message, type = 'info', duration = 3000) {
    this.notifications.show(message, type, duration);
  },

  // ─────────────────────────────────────────────────────────────
  // SPLASH SCREEN SHORTCUTS
  // ─────────────────────────────────────────────────────────────

  /**
   * Hide splash screen (game starting)
   */
  hideSplashScreen() {
    this.lockerScreen?.hide();
    this.accountScreen?.hide();
    this.leaderboardScreen?.hide();
    this.headerNav?.hide();          // lobby chrome: gone for the duration of a match
    this.splashScreen.hide();
    this.setCursorHidden(true);
  },

  /**
   * Show splash screen (returning to lobby)
   */
  showSplashScreen() {
    this.splashScreen.show();
    this.headerNav?.show();
    this.showScreen('play');
    this.setCursorHidden(false);

    // Disable Tab key game override when returning to lobby
    window._gameTabOverride = false;
  },

  /**
   * Update lobby UI with room data
   * @param {Object} roomData - Room state data
   */
  updateLobbyUI(roomData) {
    this.splashScreen.updateLobbyUI(roomData);
  },

  /**
   * Show countdown message
   * @param {number} seconds - Starting countdown value
   */
  showCountdownMessage(seconds) {
    this.splashScreen.showCountdownMessage(seconds);
  },

  /**
   * Update countdown display (server-driven ticks)
   * @param {number} seconds - Current countdown value from server
   */
  updateCountdown(seconds) {
    this.splashScreen.updateCountdown(seconds);
  },

  /**
   * Hide countdown message
   */
  hideCountdownMessage() {
    this.splashScreen.hideCountdownMessage();
  },

  /**
   * Set ready button state
   * @param {string} state - 'joining', 'readying', 'ready'
   */
  setReadyButtonState(state) {
    this.splashScreen.setReadyButtonState(state);
  },

  /**
   * Reset splash screen to initial state
   */
  resetSplashScreen() {
    this.splashScreen.reset();
  },

  /** Lock/unlock the lobby inputs (name, Locker) — see SplashScreen.setLobbyInputsLocked */
  setLobbyInputsLocked(locked) {
    this.splashScreen?.setLobbyInputsLocked(locked);
  },

  /** Equipped-skin sprite on the Locker button */
  setLockerChip(src) {
    this.splashScreen?.setLockerChip(src);
  },

  /** Open the cosmetics locker (Locker nav item) */
  openLocker() {
    return this.showScreen('locker');
  },

  /**
   * Set available game modes in the lobby selector
   * @param {Array} modes
   * @param {string} defaultModeId
   */
  setGameModes(modes = [], defaultModeId) {
    if (this.splashScreen) {
      this.splashScreen.setModeList(modes, defaultModeId);
    }
  },

  /**
   * Get the currently selected lobby game mode ID
   * @returns {string|null}
   */
  getSelectedGameModeId() {
    return this.splashScreen?.getSelectedModeId() || null;
  },

  // ─────────────────────────────────────────────────────────────
  // GAME END SCREEN SHORTCUTS
  // ─────────────────────────────────────────────────────────────

  /**
   * Show game end screen
   * @param {Object} data - Game end data (victory, stats, etc.)
   */
  showGameEndScreen(data) {
    this.gameEndScreen.show(data);
    this.setCursorHidden(false);
  },

  /**
   * Show the victory celebration, which hands off to the recap screen
   * @param {Object} data - Game end data (victory, stats, etc.)
   */
  showVictoryScreen(data) {
    this.gameEndScreen.showVictory(data);
    this.setCursorHidden(false);
  },

  /**
   * Hide game end screen
   */
  hideGameEndScreen() {
    this.gameEndScreen.hide();
    if (this.gameState?.isInGame) {
      this.setCursorHidden(true);
    }
  },

  applyRankedResult(data) {
    this.gameEndScreen?.applyRankedResult?.(data);
  },

  // ─────────────────────────────────────────────────────────────
  // COMBINED OPERATIONS
  // ─────────────────────────────────────────────────────────────

  /**
   * Handle return to lobby (combines multiple UI operations)
   */
  returnToLobby() {
    this.hideGameEndScreen();
    this.resetSplashScreen();
    this.showSplashScreen();
  }
};

// Export for use in other modules
// For now, UIManager is a global object loaded via script tag

export { UIManager };
